// Main stats grid (nutrition / fitness / sleep cards + sparklines + pantry picks) — extracted from pages/Dashboard.jsx.
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Apple, CheckCircle2, Clock, Droplets, Dumbbell, Flame, Loader2, Moon, Plane, Target, TrendingUp } from 'lucide-react';
import { MetricSparkline } from '../MetricSparkline';
import { PantryQuickPicks } from '../PantryQuickPicks';

export function StatsGrid({
    data, item, language, loadData, openMetric,
    sparkData, sparkLoading, sparkRange, setSparkRange, reloadSparklines,
    waterProgress, sleepProgress, calorieProgress, sparkM,
}) {
    return (
        <>
                {/* Main Stats Grid */}
                <div className="flex items-center justify-between gap-3 -mb-2 px-1" data-testid="sparkline-range-toggle">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground">
                            {language === 'de' ? 'Trends' : 'Trends'}
                        </span>
                        {sparkLoading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                    </div>
                    <div className="flex gap-1">
                        {[7, 30, 90].map((d) => (
                            <button
                                key={d}
                                onClick={() => setSparkRange(d)}
                                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border transition-colors ${
                                    sparkRange === d
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'border-border text-muted-foreground hover:bg-muted'
                                }`}
                                data-testid={`spark-range-${d}`}
                            >
                                {d}T
                            </button>
                        ))}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Nutrition Card */}
                    <motion.div variants={item}>
                        <Card className="module-nutrition border hover-lift card-interactive h-full" data-testid="nutrition-card">
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                                        <Apple className="w-5 h-5 icon-nutrition" />
                                        Ernährung
                                    </CardTitle>
                                    <Badge variant="outline" className="text-xs">Heute</Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="flex items-center gap-1">
                                            <Flame className="w-4 h-4 text-orange-500" />
                                            Kalorien
                                        </span>
                                        <span className="font-medium">
                                            {data?.nutrition?.calories || 0} / {data?.nutrition?.calorie_goal || 2000}
                                        </span>
                                    </div>
                                    <Progress value={Math.min(calorieProgress, 100)} className="h-2" />
                                </div>
                                <div>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="flex items-center gap-1">
                                            <Droplets className="w-4 h-4 text-blue-500" />
                                            Wasser
                                        </span>
                                        <span className="font-medium">
                                            {data?.nutrition?.water_ml || 0} / {data?.nutrition?.water_goal || 2500} ml
                                        </span>
                                    </div>
                                    <Progress value={Math.min(waterProgress, 100)} className="h-2" />
                                </div>
                                <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                                    {(() => {
                                        const weight = data?.user?.weight || 75;
                                        const level = data?.user?.fitness_level || 'beginner';
                                        const proteinPerKg = level === 'advanced' ? 2.0 : level === 'intermediate' ? 1.6 : 1.2;
                                        const goalCal = data?.user?.calorie_goal || 2000;
                                        // Use custom goals if set (> 0), otherwise auto-calculate
                                        const customP = data?.user?.protein_goal_g;
                                        const customC = data?.user?.carbs_goal_g;
                                        const customF = data?.user?.fat_goal_g;
                                        const recProtein = (customP && customP > 0) ? customP : Math.round(weight * proteinPerKg);
                                        const recFat = (customF && customF > 0) ? customF : Math.round(weight * 1.0);
                                        const recCarbs = (customC && customC > 0) ? customC : Math.max(Math.round((goalCal - recProtein * 4 - recFat * 9) / 4), 100);
                                        const actProtein = Math.round(data?.nutrition?.protein || 0);
                                        const actCarbs = Math.round(data?.nutrition?.carbs || 0);
                                        const actFat = Math.round(data?.nutrition?.fat || 0);
                                        return [
                                            { label: 'Protein', actual: actProtein, target: recProtein, unit: 'g' },
                                            { label: 'Carbs', actual: actCarbs, target: Math.max(recCarbs, 100), unit: 'g' },
                                            { label: 'Fett', actual: actFat, target: recFat, unit: 'g' },
                                        ].map((m, i) => {
                                            const pct = Math.min(m.actual / m.target * 100, 100);
                                            const isLow = m.actual < m.target * 0.5 && m.actual > 0;
                                            return (
                                                <div key={i} className="text-center">
                                                    <p className={`text-lg font-bold ${isLow ? 'text-amber-600' : ''}`}>{m.actual}{m.unit}</p>
                                                    <div className="h-1 bg-muted rounded-full mt-1 mb-0.5">
                                                        <div className={`h-full rounded-full ${pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${pct}%` }} />
                                                    </div>
                                                    <p className="text-[9px] text-muted-foreground">{m.label} / {m.target}{m.unit}</p>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                                {/* Sparklines: Calories & Water */}
                                <div className="grid grid-cols-2 gap-3 pt-2 border-t" data-testid="nutrition-sparklines">
                                    <MetricSparkline
                                        metric={sparkM.calories}
                                        label={language === 'de' ? 'Kalorien Ø' : 'Calories avg'}
                                        unit="kcal"
                                        color="#f97316"
                                        testid="spark-calories"
                                        onClick={() => openMetric('calories')}
                                    />
                                    <MetricSparkline
                                        metric={sparkM.water}
                                        label={language === 'de' ? 'Wasser Ø' : 'Water avg'}
                                        unit="ml"
                                        color="#3b82f6"
                                        testid="spark-water"
                                        onClick={() => openMetric('water')}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Smart Pantry Quick-Picks — context-aware ranking by hour-of-day
                        and meal-type. Appears under nutrition card, lets users re-log
                        their most-likely-now foods in a single tap. Auto-hides when
                        the pantry is empty so first-time users don't see a stub. */}
                    <motion.div variants={item} className="col-span-1 md:col-span-2 empty:hidden">
                        <PantryQuickPicks
                            mealType={(() => {
                                const h = new Date().getHours();
                                if (h < 10) return 'breakfast';
                                if (h < 14) return 'lunch';
                                if (h < 17) return 'snack';
                                if (h < 22) return 'dinner';
                                return 'snack';
                            })()}
                            limit={8}
                            compact
                            smart
                            onLogged={loadData}
                        />
                    </motion.div>

                    {/* Sleep Card */}
                    <motion.div variants={item}>
                        <Card className="module-sleep border hover-lift card-interactive h-full" data-testid="sleep-card">
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                                        <Moon className="w-5 h-5 icon-sleep" />
                                        Schlaf
                                    </CardTitle>
                                    <Badge variant="outline" className="text-xs">7 Tage</Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-center py-4">
                                    <div className="relative">
                                        <svg className="w-24 h-24 progress-ring">
                                            <circle
                                                cx="48"
                                                cy="48"
                                                r="40"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="8"
                                                className="text-secondary"
                                            />
                                            <circle
                                                cx="48"
                                                cy="48"
                                                r="40"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="8"
                                                strokeLinecap="round"
                                                className="text-indigo-500 progress-ring-circle"
                                                strokeDasharray={`${Math.min(sleepProgress, 100) * 2.51} 251`}
                                            />
                                        </svg>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                                            <span className="text-2xl font-bold">{data?.sleep?.avg_hours || 0}h</span>
                                            <span className="text-xs text-muted-foreground">Durchschn.</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t">
                                    <div className="text-center">
                                        <p className="text-lg font-bold">{data?.sleep?.avg_quality || 0}/10</p>
                                        <p className="text-xs text-muted-foreground">Qualität</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-lg font-bold">{data?.sleep?.goal_hours || 8}h</p>
                                        <p className="text-xs text-muted-foreground">Ziel</p>
                                    </div>
                                </div>
                                {/* Sparkline: Sleep hours */}
                                <div className="pt-2 border-t" data-testid="sleep-sparkline">
                                    <MetricSparkline
                                        metric={sparkM.sleep}
                                        label={language === 'de' ? 'Schlaf Ø' : 'Sleep avg'}
                                        unit="h"
                                        color="#6366f1"
                                        formatValue={(v) => (v ?? 0).toFixed(1)}
                                        testid="spark-sleep"
                                        onClick={() => openMetric('sleep')}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Fitness Card */}
                    <motion.div variants={item}>
                        <Card className="module-fitness border hover-lift card-interactive h-full" data-testid="fitness-card">
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                                        <Dumbbell className="w-5 h-5 icon-fitness" />
                                        Fitness
                                    </CardTitle>
                                    <Badge variant="outline" className="text-xs">Diese Woche</Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-center py-4">
                                    <div className="text-center">
                                        <div className="w-20 h-20 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mb-2">
                                            <span className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                                                {data?.fitness?.workouts_this_week || 0}
                                            </span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">Workouts</p>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t">
                                    <div className="text-center flex-1">
                                        <p className="text-lg font-bold flex items-center justify-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {data?.fitness?.minutes_this_week || 0}
                                        </p>
                                        <p className="text-xs text-muted-foreground">Minuten</p>
                                    </div>
                                    <div className="text-center flex-1">
                                        <p className="text-lg font-bold flex items-center justify-center gap-1">
                                            <Flame className="w-4 h-4" />
                                            {data?.fitness?.calories_burned || 0}
                                        </p>
                                        <p className="text-xs text-muted-foreground">kcal</p>
                                    </div>
                                </div>
                                {/* Sparklines: Steps & Workouts */}
                                <div className="grid grid-cols-2 gap-3 pt-2 border-t" data-testid="fitness-sparklines">
                                    <MetricSparkline
                                        metric={sparkM.steps}
                                        label={language === 'de' ? 'Schritte Ø' : 'Steps avg'}
                                        unit=""
                                        color="#22c55e"
                                        formatValue={(v) => Math.round(v || 0).toLocaleString()}
                                        testid="spark-steps"
                                        onClick={() => openMetric('steps')}
                                    />
                                    <MetricSparkline
                                        metric={sparkM.workouts}
                                        label={language === 'de' ? 'Workouts/Tag' : 'Workouts/day'}
                                        unit=""
                                        color="#f97316"
                                        formatValue={(v) => (v ?? 0).toFixed(1)}
                                        testid="spark-workouts"
                                        onClick={() => openMetric('workouts')}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Tasks/Travel Card */}
                    <motion.div variants={item}>
                        <Card className="border hover-lift card-interactive h-full" data-testid="tasks-travel-card">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base font-semibold flex items-center gap-2">
                                    <Target className="w-5 h-5 text-primary" />
                                    Überblick
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        <CheckCircle2 className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{data?.tasks?.pending || 0} Aufgaben</p>
                                        <p className="text-xs text-muted-foreground">offen</p>
                                    </div>
                                </div>
                                
                                {data?.travel?.current_trip ? (
                                    <div className="flex items-center gap-3 p-3 rounded-xl module-travel">
                                        <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center">
                                            <Plane className="w-5 h-5 icon-travel" />
                                        </div>
                                        <div>
                                            <p className="font-medium">{data.travel.current_trip.destination}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {data.travel.current_trip.is_active ? 'Aktive Reise' : `Geplant: ${data.travel.current_trip.start_date?.slice(0,10)}`}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                            <Plane className="w-5 h-5 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-muted-foreground">Keine Reise</p>
                                            <p className="text-xs text-muted-foreground">geplant</p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-950/30 dark:to-orange-950/30">
                                    <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/50 flex items-center justify-center">
                                        <TrendingUp className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{data?.user?.total_points || 0} Punkte</p>
                                        <p className="text-xs text-muted-foreground">gesammelt</p>
                                    </div>
                                </div>
                                {/* Sparklines: Weight & Mood */}
                                <div className="grid grid-cols-2 gap-3 pt-2 border-t" data-testid="overview-sparklines">
                                    <MetricSparkline
                                        metric={sparkM.weight}
                                        label={language === 'de' ? 'Gewicht' : 'Weight'}
                                        unit="kg"
                                        color="#a855f7"
                                        formatValue={(v) => (v ?? 0).toFixed(1)}
                                        testid="spark-weight"
                                        onClick={() => openMetric('weight')}
                                    />
                                    <MetricSparkline
                                        metric={sparkM.mood}
                                        label={language === 'de' ? 'Stimmung Ø' : 'Mood avg'}
                                        unit="/5"
                                        color="#ec4899"
                                        formatValue={(v) => (v ?? 0).toFixed(1)}
                                        testid="spark-mood"
                                        onClick={() => openMetric('mood')}
                                        streakType="mood"
                                        onFreezeApplied={reloadSparklines}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
        </>
    );
}
